import React from "react";
import { Box, Typography } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";

const ORBIT_SX = {
    "@keyframes aiOrbit": {
        "0%": { transform: "rotate(0deg)" },
        "100%": { transform: "rotate(360deg)" },
    },
};

const PULSE_SX = {
    "@keyframes aiPulse": {
        "0%, 100%": { transform: "scale(1)", opacity: 0.85 },
        "50%": { transform: "scale(1.08)", opacity: 1 },
    },
};

const SHIMMER_SX = {
    "@keyframes aiShimmer": {
        "0%": { backgroundPosition: "-200% 0" },
        "100%": { backgroundPosition: "200% 0" },
    },
};

const BOUNCE_SX = {
    "@keyframes aiBounceDot": {
        "0%, 80%, 100%": { transform: "translateY(0)", opacity: 0.4 },
        "40%": { transform: "translateY(-5px)", opacity: 1 },
    },
};

const BAR_SX = {
    "@keyframes aiBarGrow": {
        "0%": { width: "18%" },
        "50%": { width: "72%" },
        "100%": { width: "28%" },
    },
};

/**
 * Full-panel loader for the AI Powered email preview pane.
 */
export const AiPreviewCraftingLoader = () => (
    <Box
        sx={{
            ...ORBIT_SX,
            ...PULSE_SX,
            ...SHIMMER_SX,
            ...BAR_SX,
            height: "100%",
            minHeight: 280,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2.5,
            px: 3,
            textAlign: "center",
        }}
    >
        <Box
            sx={{
                position: "relative",
                width: 72,
                height: 72,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: "primary.light",
                    borderTopColor: "primary.main",
                    animation: "aiOrbit 1.1s linear infinite",
                }}
            />
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "aiPulse 1.6s ease-in-out infinite",
                    boxShadow: "0 8px 24px rgba(25, 118, 210, 0.28)",
                }}
            >
                <AutoAwesome sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
        </Box>

        <Box>
            <Typography sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                Crafting your email…
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Preview updates when the response is ready.
            </Typography>
        </Box>

        <Box sx={{ width: "100%", maxWidth: 280, display: "flex", flexDirection: "column", gap: 1 }}>
            {[100, 78, 90].map((width, index) => (
                <Box
                    key={index}
                    sx={{
                        height: 10,
                        width: `${width}%`,
                        borderRadius: 999,
                        alignSelf: index === 1 ? "center" : "stretch",
                        background:
                            "linear-gradient(90deg, rgba(25,118,210,0.08) 25%, rgba(25,118,210,0.22) 50%, rgba(25,118,210,0.08) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "aiShimmer 1.4s ease-in-out infinite",
                        animationDelay: `${index * 0.12}s`,
                    }}
                />
            ))}
            <Box
                sx={{
                    mt: 0.5,
                    height: 4,
                    borderRadius: 999,
                    bgcolor: "rgba(25,118,210,0.12)",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        borderRadius: 999,
                        bgcolor: "primary.main",
                        animation: "aiBarGrow 1.8s ease-in-out infinite",
                    }}
                />
            </Box>
        </Box>
    </Box>
);

/**
 * Single chat bubble shown while Gemini is generating a response.
 */
export const AiChatCraftingCard = () => (
    <Box
        sx={{
            ...BOUNCE_SX,
            mb: 1.5,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: "#fff",
            border: "1px solid rgba(25,118,210,0.2)",
            background:
                "linear-gradient(135deg, rgba(25,118,210,0.04) 0%, rgba(255,255,255,1) 55%)",
        }}
    >
        <Typography
            variant="caption"
            sx={{
                color: "primary.main",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
            }}
        >
            <AutoAwesome sx={{ fontSize: 14 }} />
            assistant
        </Typography>
        <Box
            sx={{
                mt: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 1,
            }}
        >
            <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 600 }}>
                Crafting…
            </Typography>
            <Box sx={{ display: "flex", gap: 0.4, ml: 0.25 }}>
                {[0, 1, 2].map((dot) => (
                    <Box
                        key={dot}
                        sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            animation: "aiBounceDot 1s ease-in-out infinite",
                            animationDelay: `${dot * 0.15}s`,
                        }}
                    />
                ))}
            </Box>
        </Box>
    </Box>
);
